import { Injectable } from '@angular/core';
import { BillingService, Currency, ProductPricing } from '@galaxy/billing';
import { BilledProduct } from '@vendasta/shared';
import { Observable, zip } from 'rxjs';
import { filter, map, publishReplay, refCount, shareReplay } from 'rxjs/operators';
import { mergePricing } from './pricing-adapter';

export interface PricingServiceInterface {
  getProductPricing(partnerId: string, level: string): Observable<ProductPricing>;
}

@Injectable({ providedIn: 'root' })
export class PricingService implements PricingServiceInterface {
  constructor(private billingService: BillingService) {}

  getProductPricing(merchantId: string, sku: string): Observable<ProductPricing> {
    return this.billingService.getMultiProductPricing(merchantId, [sku]).pipe(
      map((pricings) => pricings[sku]),
      shareReplay(1),
    );
  }

  getMultiProductPricing(merchantId: string, skus: string[]): Observable<{ [sku: string]: ProductPricing }> {
    return this.billingService.getMultiProductPricing(merchantId, skus).pipe(publishReplay(1), refCount());
  }

  getRetailPricing(merchantId: string, currency: Currency, sku: string): Observable<ProductPricing> {
    return this.getMultiRetailPricing(merchantId, [sku], currency).pipe(
      map((pricings) => pricings[sku]),
      shareReplay(1),
    );
  }

  getMultiRetailPricing(
    merchantId: string,
    skus: string[],
    currency?: Currency,
    groupId?: string,
  ): Observable<{ [sku: string]: ProductPricing }> {
    return this.billingService
      .getMultiRetailPricing(merchantId, currency, skus, groupId)
      .pipe(publishReplay(1), refCount());
  }

  // Deprecated, use getMultiProductPricing and supply your own app data
  // Note: The numeric "price" and "productCategory" fields of the BilledProduct are no longer supported
  getBilledProducts(merchantId: string, skus: string[]): Observable<BilledProduct[]> {
    return zip(
      this.billingService.listProducts(merchantId, null, { skus: skus }, '', skus.length),
      this.getMultiProductPricing(merchantId, skus),
    ).pipe(
      filter(([products, pricingMap]) => !!products && !!pricingMap),
      map(([products, pricingMap]) => {
        return products.results.map((product) => (product ? mergePricing(product, pricingMap[product.sku]) : null));
      }),
      shareReplay(1),
    );
  }
}
